import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User, Users, GraduationCap, CheckCircle2 } from 'lucide-react';
export function PortalsSection() {
  const [activeTab, setActiveTab] = useState('leerling');
  const tabs = [{
    id: 'leerling',
    label: 'Voor Leerlingen',
    icon: <User className="w-4 h-4" data-id="element-183" />
  }, {
    id: 'ouder',
    label: 'Voor Ouders',
    icon: <Users className="w-4 h-4" data-id="element-184" />
  }, {
    id: 'leraar',
    label: 'Voor Leraren',
    icon: <GraduationCap className="w-4 h-4" data-id="element-185" />
  }];
  const content = {
    leerling: {
      title: 'Leren voelt als een spel',
      description: 'Een eigen dashboard waar groei wordt gevierd en het volgende doel altijd duidelijk is.',
      features: ['Persoonlijk dashboard met aanbevolen vakken', 'XP verdienen en levels omhoog gaan', 'Confetti-vieringen bij het behalen van mastery', 'Inzicht in eigen voortgang per vak'],
      color: 'text-orange-500',
      bg: 'bg-orange-500'
    },
    ouder: {
      title: 'Altijd op de hoogte',
      description: 'Weet precies hoe je kind ervoor staat, zonder zelf sommen na te hoeven kijken.',
      features: ['Voortgang per kind per vak in één overzicht', 'Weekoverzichten en recente activiteit', 'Concrete ouder-tips voor thuis oefenen', 'Eenvoudig schakelen tussen meerdere kinderen'],
      color: 'text-emerald-600',
      bg: 'bg-emerald-600'
    },
    leraar: {
      title: 'De hele klas in beeld',
      description: 'Data-gedreven inzichten om gerichte instructie te geven waar het echt nodig is.',
      features: ['Klasgemiddelden en voortgang per vak', "'Aandacht nodig' signalering voor specifieke leerlingen", 'Weekinzichten: wie heeft geoefend, wat is het meest geoefend?', 'Gedetailleerde statistieken en developer tools'],
      color: 'text-amber-600',
      bg: 'bg-amber-600'
    }
  };
  const activeContent = content[activeTab as keyof typeof content];
  return <section className="py-16 lg:py-24 bg-white border-y border-slate-100" data-id="element-186">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-187">
        <div className="text-center max-w-2xl mx-auto mb-12" data-id="element-188">
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-950 mb-4 tracking-tighter" data-id="element-189">
            Multi-Stakeholder Portalen
          </h2>
          <p className="text-lg text-slate-600" data-id="element-190">
            Een compleet ecosysteem met unieke interfaces voor iedereen die
            betrokken is bij het leerproces.
          </p>
        </div>

        {/* Tabs Navigation */}
        <div className="flex flex-wrap justify-center gap-2 mb-12" data-id="element-191">
          {tabs.map(tab => {
          const isActive = activeTab === tab.id;
          const tabContent = content[tab.id as keyof typeof content];
          return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`relative flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold transition-colors ${isActive ? 'text-white' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-192">
                {isActive && <motion.div layoutId="activePortalTab" className={`absolute inset-0 rounded-lg ${tabContent.bg} -z-10`} transition={{
              type: 'spring',
              bounce: 0.2,
              duration: 0.6
            }} data-id="element-193" />}
                <span className="relative z-10 flex items-center gap-2" data-id="element-194">
                  {tab.icon}
                  {tab.label}
                </span>
              </button>;
        })}
        </div>

        {/* Tab Content */}
        <div className="min-h-[280px]" data-id="element-195">
          <AnimatePresence mode="wait" data-id="element-196">
            <motion.div key={activeTab} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} transition={{
            duration: 0.2
          }} className="max-w-3xl mx-auto text-center" data-id="element-197">
              <h3 className="text-2xl font-bold text-slate-950 mb-4" data-id="element-198">
                {activeContent.title}
              </h3>
              <p className="text-lg text-slate-600 mb-10" data-id="element-199">
                {activeContent.description}
              </p>
              <ul className="grid sm:grid-cols-2 gap-4 text-left" data-id="element-200">
                {activeContent.features.map((feature, idx) => <li key={idx} className="flex items-start gap-3 bg-slate-50 p-4 rounded-xl border border-slate-100" data-id="element-201">
                    <CheckCircle2 className={`w-5 h-5 ${activeContent.color} shrink-0`} data-id="element-202" />
                    <span className="text-slate-700 text-sm font-bold" data-id="element-203">
                      {feature}
                    </span>
                  </li>)}
              </ul>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>;
}